import React, { Fragment, useMemo } from 'react';

import classNames from 'classnames';
import moment from 'moment';

import { KANBAN_COLUMNS, kanbanColumnFor, money, priorityPillClass } from './planHelpers';

import CalendarIcon from 'Assets/icons/calendar.svg';

/**
 * IB-700 (v14) — Plan tab Kanban board keyed on the five APPROVAL-PROCESS steps
 * (To raise → PR submitted → PR approved → EA/DC submitted → Approved & committed), driven by each
 * procurement's real PR/EA/DC approval state via `kanbanColumnFor`. NOTE: this is deliberately NOT
 * keyed on sourcing status — a procurement sits in its furthest-reached approval step.
 *
 * Cards are the same procurement records shown in the register, so the board honours whatever
 * search/priority/grouping filtering produced `items`.
 */
const KanbanCard = ({ item, currencySymbol, onView }) => {
    const awardDate = item.target_contract_award_date
        ? $t('award {{date}}', { date: moment(item.target_contract_award_date, 'YYYY-MM-DD').format('D MMM YY') })
        : null;

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => onView && onView(item)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onView && onView(item)}
            className="tw-cursor-pointer tw-rounded-lg tw-border tw-border-grey tw-bg-white tw-p-3 tw-text-start tw-transition-shadow hover:tw-shadow-primary focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue"
        >
            {item.category?.name && (
                <div className="tw-text-[0.65rem] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-charcoal-50">{item.category.name}</div>
            )}
            <div className="tw-text-sm tw-font-medium tw-text-charcoal">{item.name}</div>

            <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-1">
                <span className="tw-inline-flex tw-rounded tw-bg-blue-15 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-blue-75">
                    {money(currencySymbol, item.value)}
                </span>
                {item.vik > 0 && (
                    <span className="tw-inline-flex tw-rounded tw-bg-green-25 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-charcoal-75">
                        {$t('VIK {{amount}}', { amount: money(currencySymbol, item.vik) })}
                    </span>
                )}
                {item.priority_label && (
                    <span
                        className={classNames(
                            'tw-inline-flex tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold',
                            priorityPillClass(item.priority_label),
                        )}
                    >
                        {item.priority_label}
                    </span>
                )}
            </div>

            {!!(item.departments_names && item.departments_names.length) && (
                <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-1">
                    {item.departments_names.map((name) => (
                        <span key={name} className="tw-inline-flex tw-rounded tw-bg-charcoal-10 tw-px-1.5 tw-py-0.5 tw-text-[0.65rem] tw-text-charcoal-75">
                            {name}
                        </span>
                    ))}
                </div>
            )}

            {(awardDate || item.owner_name) && (
                <div className="tw-mt-3 tw-flex tw-items-center tw-justify-between tw-text-xs tw-text-charcoal-50">
                    <span className="tw-inline-flex tw-items-center tw-gap-1">
                        {awardDate && (
                            <Fragment>
                                <CalendarIcon className="tw-h-3 tw-w-3" />
                                {awardDate}
                            </Fragment>
                        )}
                    </span>
                    {item.owner_name && <span>{item.owner_name}</span>}
                </div>
            )}
        </div>
    );
};

const PlanKanban = ({ items, currencySymbol, onView }) => {
    const columns = useMemo(() => {
        const buckets = Object.fromEntries(KANBAN_COLUMNS.map(({ id }) => [id, []]));

        (items ?? []).forEach((item) => {
            buckets[kanbanColumnFor(item)].push(item);
        });

        return KANBAN_COLUMNS.map((column) => ({ ...column, items: buckets[column.id] }));
    }, [items]);

    return (
        <div className="tw-grid tw-grid-cols-5 tw-gap-3">
            {columns.map(({ id, label, dot, items: columnItems }) => (
                <div key={id} className="tw-flex tw-flex-col tw-rounded-lg tw-bg-charcoal-10 tw-p-3">
                    <div className="tw-mb-3 tw-flex tw-items-center tw-gap-1.5">
                        <span className={classNames('tw-h-2 tw-w-2 tw-rounded-full tw-shrink-0', dot)} />
                        <span className="tw-text-sm tw-font-semibold tw-text-charcoal">{label}</span>
                        <span className="tw-ms-auto tw-text-xs tw-text-charcoal-50">{columnItems.length}</span>
                    </div>
                    <div className="tw-flex tw-flex-col tw-gap-2">
                        {columnItems.length ? (
                            columnItems.map((item) => <KanbanCard key={item.id} item={item} currencySymbol={currencySymbol} onView={onView} />)
                        ) : (
                            <p className="tw-py-4 tw-text-center tw-text-xs tw-text-charcoal-50">{$t('No procurements')}</p>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default PlanKanban;
